import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { getGoogleClientId } from '@/lib/googleClientId';
import { adsErrorKey } from '@/lib/adsConnectErrors';
import { writeAdsConnectReturn, type AdsConnectReturn } from '@/lib/adsConnectReturn';
import { toast } from 'sonner';

/**
 * Verbindet das EIGENE Google-Ads-Konto des Kunden (Spec AP3).
 *
 * Dieser Knopf ist bewusst KEINE Erweiterung von `GoogleOAuthButton`. Er
 * unterscheidet sich in drei Punkten, und jeder einzelne waere als
 * Wiederverwendung ein Schaden:
 *
 * 1. ANDERER SCOPE. Angefordert wird ausschliesslich `auth/adwords`. Der Scope
 *    ist bei Google noch nicht freigegeben, deshalb darf er in keiner der
 *    bestehenden Scope-Listen auftauchen (Spec G1). Ein Produktions-Flow, der
 *    einen unverifizierten Scope anfordert, verbrennt Googles eigene Worte
 *    zufolge die unverified-user-Quota und stoert Bestandsnutzer.
 *    Gepinnt von `src/test/adsScopeIsolation.test.ts`.
 *
 * 2. ANDERER NACHRICHTENTYP. `GOOGLE_OAUTH_SUCCESS` wird von mehreren
 *    gleichzeitig gemounteten Komponenten mitgehoert; `GoogleOAuthButton`
 *    startet daraufhin `fetch-account-data` und oeffnet den Sync-Dialog
 *    (`GoogleOAuthButton.tsx:95-104`), `ReauthDialog` fuehrt seinen Retry aus
 *    (`ReauthDialog.tsx:134`). Eine Ads-Verbindung wuerde damit einen
 *    vollstaendigen Merchant-Sync ausloesen. Deshalb eigene Typen.
 *
 * 3. ANDERER ZIELZUSTAND. Es entsteht keine `google_accounts`-Zeile, sondern
 *    eine oder mehrere `google_ads_accounts`-Zeilen.
 *
 * Copy ueber den Namespace `adsPerformance`, DE und EN (die Englisch-only-Regel
 * aus Spec F3 ist mit der Google-Freigabe vom 22.08.2026 hinfaellig). Der Knopf
 * steht seit Spec 2026-09-09 im Feed-Check und in Settings > Accounts, nicht
 * mehr nur auf der internen Route.
 */

/** Der Ads-Scope. Steht ausschliesslich hier, in keiner bestehenden Scope-Liste. */
const GOOGLE_ADS_SCOPE = 'https://www.googleapis.com/auth/adwords';

export interface ConnectGoogleAdsButtonProps {
  /** Bekommt den Antwortkoerper von `handleAdsConnect` (accounts + Vorauswahl). */
  onConnected?: (data: unknown) => void;
  className?: string;
  /**
   * Wohin der VOLLBILD-Fall (Popup blockiert) zurueckkehren soll. Ohne Angabe
   * bleibt es beim heutigen Ziel `/internal/ads-performance` -- dem Ort, von
   * dem dieser Knopf bis Spec 2026-09-09 als einziges aufgerufen wurde.
   */
  returnTo?: AdsConnectReturn;
  /** Voreinstellung `outline`; `default` fuer den einen Knopf einer Flaeche. */
  variant?: 'outline' | 'default';
  /**
   * Eigene Beschriftung statt `connect.button`. Gebraucht dort, wo der Knopf
   * NICHT das erste Verbinden anbietet: nach einem Trennen bleibt die
   * `google_ads_accounts`-Zeile stehen (die RPC nullt nur die Tokens), der
   * Weg zurueck ist derselbe Knopf, und "Google Ads verbinden" waere daneben
   * die falsche Aussage. Der Ladezustand bleibt `connect.connecting`.
   */
  label?: React.ReactNode;
  /**
   * Von aussen gesperrt, zusaetzlich zum eigenen Ladezustand. Gesetzt, solange
   * ein Erst-Abruf laeuft (`AdsConnectFlow.busy`): ein zweiter Consent
   * daneben stiesse denselben 30-Tage-Pull noch einmal an, und das
   * Operationskontingent des Developer-Tokens teilen sich alle Kunden.
   */
  disabled?: boolean;
  /**
   * Kennung fuer das Auffinden von aussen. Gesetzt vom Ads-Kapitel
   * (`ads-connect-cta`): der Primaerknopf der Handlungskarte fuer Schritt 5
   * klappt das Kapitel auf und setzt den Fokus auf DIESEN Knopf, statt einen
   * zweiten Connect-Ablauf zu bauen (Spec 2026-09-16, Abschnitt 3.2).
   */
  testId?: string;
}

const ConnectGoogleAdsButton: React.FC<ConnectGoogleAdsButtonProps> = ({
  onConnected,
  className,
  returnTo,
  variant,
  label,
  disabled,
  testId,
}) => {
  const { t } = useTranslation('adsPerformance');
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  // Raeumt den laufenden Popup-Waechter (1-s-Intervall + message-Listener) beim
  // Unmount. Ohne das lief das Intervall nach dem Unmount weiter und rief
  // setIsConnecting auf einer Komponente, die es nicht mehr gab; in der CI
  // (26.09.2026) traf der Tick nach dem Abbau von jsdom ein und riss den
  // ganzen Testlauf mit "ReferenceError: window is not defined". In der App
  // ist es derselbe Fall, wenn der Nutzer bei offenem Popup navigiert.
  const stopWatchingRef = useRef<(() => void) | null>(null);
  useEffect(() => () => {
    stopWatchingRef.current?.();
    stopWatchingRef.current = null;
  }, []);

  const handleConnect = async () => {
    if (isConnecting) return;
    // Zweite Linie neben dem `disabled` am Knopf: ein Klick aus einem Test
    // oder aus der Tastaturbedienung darf die Sperre nicht umgehen.
    if (disabled) return;
    if (!user) {
      toast.error(t('connect.authRequired'), { description: t('connect.authRequiredDesc') });
      return;
    }

    setIsConnecting(true);

    // Popup synchron oeffnen, sonst blockt der Browser es als nicht
    // nutzerausgeloest (gleiches Muster wie GoogleOAuthButton).
    const popup = window.open(
      'about:blank',
      'google-ads-oauth',
      'width=500,height=600,scrollbars=yes,resizable=yes',
    );

    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      // Retry + localStorage-Fallback (Sentry DENO-T): ein 502/503 auf dem
      // Preflight darf den Connect nicht mehr toeten, bevor er beginnt.
      const clientId = await getGoogleClientId();

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      // NUR der Ads-Scope. Alles andere haengt bereits am Konto.
      authUrl.searchParams.set('scope', GOOGLE_ADS_SCOPE);
      authUrl.searchParams.set('state', `ads:${user.id}`);
      // Offline-Zugriff: ohne Refresh-Token kann der naechtliche Pull nie laufen.
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      // DER TRAGENDE PARAMETER, nicht Kosmetik:
      //
      // Mit `include_granted_scopes=true` liefert Google im `scope`-Feld der
      // Token-Antwort die VEREINIGUNG aller bisher erteilten Scopes zurueck
      // (granted-scopes.ts:25-28). Damit bleibt `hasContentScope(tokens.scope)`
      // wahr und `resolveAuthStatus` liefert weiterhin 'active'.
      //
      // Ohne ihn kaeme aus diesem Consent nur `auth/adwords` zurueck. Ein
      // Bestandskunde, der hier klickt, kippte damit auf 'scope_missing' und
      // bekaeme das Reparatur-Banner ueber die gesamte App -- fuer eine
      // Merchant-Verbindung, die vollstaendig intakt ist.
      authUrl.searchParams.set('include_granted_scopes', 'true');

      if (popup && !popup.closed) {
        popup.location.href = authUrl.toString();
      } else {
        // Popup blockiert: Vollbild-Weiterleitung. GoogleCallback schickt den
        // Nutzer ueber den Marker zurueck (Feed-Check, Settings) oder ohne
        // Marker auf das interne Panel.
        if (returnTo) writeAdsConnectReturn(returnTo);
        window.location.href = authUrl.toString();
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        // Eigene Nachrichtentypen. Siehe Punkt 2 im Kommentarkopf: auf
        // GOOGLE_OAUTH_SUCCESS hoeren fremde Komponenten mit.
        if (event.data.type === 'GOOGLE_ADS_OAUTH_SUCCESS') {
          popup?.close();
          setIsConnecting(false);
          stopWatching();

          const data = event.data.data;
          const preselected = data?.preselectedAdsCustomerId;
          toast.success(t('connect.successTitle'), {
            description: preselected
              ? t('connect.successDescPreselected')
              : t('connect.successDescChoose'),
          });
          onConnected?.(data);
        } else if (event.data.type === 'GOOGLE_ADS_OAUTH_ERROR') {
          popup?.close();
          setIsConnecting(false);
          stopWatching();

          toast.error(t('connect.errorTitle'), {
            description: t(adsErrorKey(event.data.code)),
          });
        }
      };

      window.addEventListener('message', handleMessage);

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          setIsConnecting(false);
          stopWatching();
        }
      }, 1000);
      // Eine Stelle, die beide Waechter beendet: Erfolg, Fehler, geschlossenes
      // Popup und Unmount rufen sie; danach ist nichts mehr registriert.
      const stopWatching = () => {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        stopWatchingRef.current = null;
      };
      stopWatchingRef.current = stopWatching;
    } catch (error) {
      setIsConnecting(false);
      console.error('Error initiating Google Ads OAuth:', error);
      Sentry.captureException(error, { tags: { feature: 'ads-performance', action: 'initiate-ads-oauth' } });
      toast.error(t('connect.errorTitle'), { description: t('connect.initFailed') });
    }
  };

  return (
    <Button onClick={handleConnect} variant={variant ?? 'outline'} className={className} disabled={isConnecting || disabled} data-testid={testId}>
      {isConnecting ? t('connect.connecting') : (label ?? t('connect.button'))}
    </Button>
  );
};

export default ConnectGoogleAdsButton;
